import { useEffect, useCallback, useRef } from 'react'
import { useIntl } from 'react-intl'
import { EnergyMode } from '@vacationtracker/shared/types/user-insight'
import { energyLevelTracker } from '../services'
import { EnergyLevelData, PTOSuggestionsResponse } from '../types'
import { IGetLeaveTypesList } from '../../../types/leave-types'

interface UseEnergyLevelTrackingProps {
  isFeatureEnabled: boolean
  energyData: EnergyLevelData | null
  ptoSuggestions: PTOSuggestionsResponse | null
  leaveTypes: IGetLeaveTypesList['getLeaveTypeList']
  loading: boolean
  ptoLoading: boolean
  error: string | null
  ptoError: string | null
}

interface TrackingCallbacks {
  handleSuggestedLeaveClick: (
    suggestion: any,
    suggestionIndex: number,
    isBestSuggestion: boolean,
    clickSource?: 'suggestion_card' | 'quick_action' | 'detailed_view'
  ) => void
  handleTooltipView: (
    tooltipType: 'energy_score' | 'trend_point' | 'factor_breakdown' | 'pto_suggestion' | 'department_comparison' | 'energy_mode',
    tooltipContent: string,
    elementHovered: string,
    section: 'energy_gauge' | 'trend_chart' | 'suggestions' | 'score_breakdown',
    hoverDurationMs: number,
    tooltipTriggeredBy?: 'mouse_hover' | 'touch' | 'keyboard_focus',
    hoveredScore?: number,
    hoveredDate?: string,
    hoveredWeek?: number,
    hoveredYear?: number,
    chartDataset?: string
  ) => void
  handleActionTaken: (
    actionType: 'button_click' | 'chart_interaction' | 'link_click' | 'gauge_click',
    actionName: string,
    elementType: 'button' | 'link' | 'chart' | 'gauge' | 'card' | 'suggestion',
    section: 'header' | 'energy_gauge' | 'trend_chart' | 'suggestions' | 'score_breakdown',
    ledToNavigation?: boolean,
    destinationUrl?: string
  ) => void
  handleFeedbackLinkClick: (
    location: 'header' | 'footer' | 'energy_gauge' | 'suggestions' | 'score_breakdown' | 'error_state'
  ) => void
  handleFeedbackSubmit: (
    feedbackReason: string,
    userSuggestedScore: number | undefined,
    feedbackText: string,
    timeToSubmitMs: number,
    submissionMethod?: 'quick_rating' | 'detailed_form' | 'inline_feedback',
    allowContact?: boolean
  ) => void
  handleViewFullTeamCalendarClick: () => void
}

/**
 * Custom hook for managing energy level tracking
 * Handles initialization, cleanup, and all tracking callbacks
 */
export const useEnergyLevelTracking = ({
  isFeatureEnabled,
  energyData,
  ptoSuggestions,
  leaveTypes,
  loading,
  ptoLoading,
  error,
  ptoError,
}: UseEnergyLevelTrackingProps): TrackingCallbacks => {
  const { formatMessage } = useIntl()
  const loadStartTime = useRef<number>(Date.now())
  const hasTrackedPageVisit = useRef<boolean>(false)

  // Initialize tracking and cleanup
  useEffect(() => {
    if (!isFeatureEnabled) {
      return
    }

    energyLevelTracker.init()

    return () => {
      energyLevelTracker.cleanup()
    }
  }, [isFeatureEnabled])

  // Track page visit once data is loaded
  useEffect(() => {
    if (!hasTrackedPageVisit.current && energyData && !loading && !ptoLoading) {
      // Update energy data for engagement tracking
      energyLevelTracker.updateEnergyData(energyData.currentScore.current, energyData.energyMode)

      energyLevelTracker.trackVisited(energyData, ptoSuggestions || undefined, leaveTypes)
      hasTrackedPageVisit.current = true

      // Track load time
      const totalLoadTime = Date.now() - loadStartTime.current
      energyLevelTracker.trackLoadTime({
        totalLoadTime,
        energyDataLoadTime: totalLoadTime,
        ptoSuggestionsLoadTime: totalLoadTime,
        dataSize: JSON.stringify({ energyData, ptoSuggestions }).length / 1024,
      })
    }
  }, [energyData, ptoSuggestions, loading, ptoLoading, leaveTypes])

  // Track errors
  useEffect(() => {
    if (error) {
      energyLevelTracker.trackError({
        errorType: error === formatMessage({ id: 'energyLevel.noDataTitle' }) ? 'energy_load_error' : 'api_error',
        errorMessage: error,
        sectionAffected: 'energy_data',
        hasCachedData: false,
        retryAttempted: false,
        autoRecoveryPossible: true,
      })
    }
  }, [error, formatMessage])

  useEffect(() => {
    if (ptoError) {
      energyLevelTracker.trackError({
        errorType: 'api_error',
        errorMessage: ptoError,
        sectionAffected: 'pto_suggestions',
        hasCachedData: !!energyData,
        retryAttempted: false,
        autoRecoveryPossible: true,
      })
    }
  }, [ptoError, energyData])

  // Callback handlers for tracking interactions
  const handleSuggestedLeaveClick = useCallback((
    suggestion: any,
    suggestionIndex: number,
    isBestSuggestion: boolean,
    clickSource?: 'suggestion_card' | 'quick_action' | 'detailed_view'
  ) => {
    if (energyData && ptoSuggestions) {
      const bestSuggestion = ptoSuggestions.suggestions.find(s => s.isBestSuggestion)
      energyLevelTracker.trackSuggestedLeaveClicked({
        suggestion,
        suggestionIndex,
        isBestSuggestion,
        currentEnergyScore: energyData.currentScore.current,
        energyMode: energyData.energyMode,
        clickSource,
        totalSuggestions: ptoSuggestions.suggestions.length,
        bestSuggestion,
      })
    }
  }, [energyData, ptoSuggestions])

  const handleTooltipView = useCallback((
    tooltipType: 'energy_score' | 'trend_point' | 'factor_breakdown' | 'pto_suggestion' | 'department_comparison' | 'energy_mode',
    tooltipContent: string,
    elementHovered: string,
    section: 'energy_gauge' | 'trend_chart' | 'suggestions' | 'score_breakdown',
    hoverDurationMs: number,
    tooltipTriggeredBy?: 'mouse_hover' | 'touch' | 'keyboard_focus',
    hoveredScore?: number,
    hoveredDate?: string,
    hoveredWeek?: number,
    hoveredYear?: number,
    chartDataset?: string
  ) => {
    if (energyData) {
      energyLevelTracker.trackTooltipViewed({
        tooltipType,
        tooltipContent,
        elementHovered,
        section,
        hoverDurationMs,
        currentEnergyScore: energyData.currentScore.current,
        energyMode: energyData.energyMode,
        tooltipTriggeredBy,
        hoveredScore,
        hoveredDate,
        hoveredWeek,
        hoveredYear,
        chartDataset,
      })
    }
  }, [energyData])

  const handleActionTaken = useCallback((
    actionType: 'button_click' | 'chart_interaction' | 'link_click' | 'gauge_click',
    actionName: string,
    elementType: 'button' | 'link' | 'chart' | 'gauge' | 'card' | 'suggestion',
    section: 'header' | 'energy_gauge' | 'trend_chart' | 'suggestions' | 'score_breakdown',
    ledToNavigation?: boolean,
    destinationUrl?: string
  ) => {
    if (energyData) {
      energyLevelTracker.trackActionTaken({
        actionType,
        actionName,
        elementType,
        section,
        currentEnergyScore: energyData.currentScore.current,
        energyMode: energyData.energyMode,
        isLoading: loading || ptoLoading,
        ledToNavigation,
        destinationUrl,
      })
    }
  }, [energyData, loading, ptoLoading])

  const handleFeedbackLinkClick = useCallback((
    location: 'header' | 'footer' | 'energy_gauge' | 'suggestions' | 'score_breakdown' | 'error_state'
  ) => {
    if (energyData) {
      energyLevelTracker.trackFeedbackLinkClicked({
        feedbackLinkLocation: location,
        currentEnergyScore: energyData.currentScore.current,
        energyMode: energyData.energyMode,
        daysSinceLastBreak: energyData.daysSinceLastBreak,
        leaveTypes,
      })
    }
  }, [energyData, leaveTypes])

  const handleFeedbackSubmit = useCallback((
    feedbackReason: string,
    userSuggestedScore: number | undefined,
    feedbackText: string,
    timeToSubmitMs: number,
    submissionMethod?: 'quick_rating' | 'detailed_form' | 'inline_feedback',
    allowContact?: boolean,
    reportedEnergyMode?: EnergyMode
  ) => {
    if (energyData) {
      energyLevelTracker.trackFeedbackSubmitted({
        feedbackReason,
        userSuggestedScore,
        currentCalculatedScore: energyData.currentScore.current,
        energyMode: energyData.energyMode,
        reportedEnergyMode: reportedEnergyMode,
        daysSinceLastBreak: energyData.daysSinceLastBreak,
        feedbackText,
        timeToSubmitMs,
        submissionMethod,
        allowContact: allowContact || false,
        leaveTypes,
      })
    }
  }, [energyData, leaveTypes])

  const handleViewFullTeamCalendarClick = useCallback(() => {
    if (energyData && ptoSuggestions) {
      const bestSuggestion = ptoSuggestions.suggestions.find(s => s.isBestSuggestion)
      energyLevelTracker.trackViewFullTeamCalendarClicked({
        currentEnergyScore: energyData.currentScore.current,
        energyMode: energyData.energyMode,
        hasPtoSuggestions: (ptoSuggestions.suggestions?.length || 0) > 0,
        ptoSuggestionsCount: ptoSuggestions.suggestions?.length || 0,
        bestSuggestionDuration: bestSuggestion?.durationDays,
        bestSuggestionImprovement: bestSuggestion?.energyPrediction?.improvement,
        daysSinceLastBreak: energyData.daysSinceLastBreak,
        clickSource: 'suggestions_section',
        destinationUrl: '/app/calendar',
      })
    }
  }, [energyData, ptoSuggestions])

  return {
    handleSuggestedLeaveClick,
    handleTooltipView,
    handleActionTaken,
    handleFeedbackLinkClick,
    handleFeedbackSubmit,
    handleViewFullTeamCalendarClick,
  }
}
